import axios from 'axios';
import { BASE_URL } from '../../context/FetchContext';

export type ConnectionQuality = 'excellent' | 'good' | 'fair' | 'poor' | 'unknown';

export type VideoResolution = '480p' | '720p' | '1080p' | '1440p' | '4K';

export type LocalRecordingErrorCode =
    | 'start_failed'
    | 'fatal'
    | 'unknown';

export const RESOLUTION_OPTIONS = [
    { label: '480p' as VideoResolution, width: 854, height: 480 },
    { label: '720p' as VideoResolution, width: 1280, height: 720 },
    { label: '1080p' as VideoResolution, width: 1920, height: 1080 },
    { label: '1440p' as VideoResolution, width: 2560, height: 1440 },
    { label: '4K' as VideoResolution, width: 3840, height: 2160 },
];

export interface ParticipantStatusUpdate {
    roomName: string;
    participantIdentifier: string,
    connectionQuality?: ConnectionQuality;
    videoResolution?: VideoResolution;
    uploadProgress?: number;
    /**
     * Optional error code for client-side local recording failures.
     * Empty string clears the UI indicator (server will store the latest value).
     */
    localRecordingError?: LocalRecordingErrorCode | '';
}

/**
 * Updates participant status (connection quality, upload progress)
 * Called by guests to report their status to the server.
 * 
 * @param token - LiveKit token for authentication
 * @param update - Status fields to update
 */
export async function updateParticipantStatus(
    token: string,
    update: ParticipantStatusUpdate
): Promise<void> {
    const config = {
        headers: { Authorization: `Bearer ${token}` },
    };
    
    await axios.post(`${BASE_URL}/participant/status`, update, config);
}

/**
 * Maps LiveKit ConnectionQuality enum to our string format
 * LiveKit ConnectionQuality: Unknown = 0, Poor = 1, Good = 2, Excellent = 3
 */
export function mapLiveKitConnectionQuality(quality: number | string): ConnectionQuality {
    if (typeof quality === 'string') {
        const lowerQuality = quality.toLowerCase();
        if (lowerQuality === 'excellent') return 'excellent';
        if (lowerQuality === 'good') return 'good';
        if (lowerQuality === 'fair') return 'fair';
        if (lowerQuality === 'poor') return 'poor';
        return 'unknown';
    }
    
    switch (quality) {
        case 3:
            return 'excellent';
        case 2:
            return 'good';
        case 1:
            return 'poor';
        default:
            return 'unknown';
    }
}
